import { Models, Services, Types, Utils } from '@sendum/core'
import React, { useMemo, useRef, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import { ShipmentTimelineSegment } from './ShipmentTimelineSegment'
import { TimelineRootState } from './store/TimelineStore'
import classes from './TimelineContent.module.scss'

interface IProps {
    noDataText?: JSX.Element
    onChangeSegmentsCount?: (id: string) => void
}

export const TimelineContent = ({ noDataText, onChangeSegmentsCount }: IProps): JSX.Element => {
    const prevSegmentsCountRef = useRef<number>()
    const segments = useSelector<TimelineRootState, Models.Segment[]>(
        (state: TimelineRootState) => state.Timeline.segments,
        shallowEqual
    )
    const config = useSelector<TimelineRootState, Types.TTimeLineAttribute | undefined>(
        (state: TimelineRootState) => state.Timeline.config,
        shallowEqual
    )

    const [startShipmentDate, setStartShipmentDate] = useState<string>('')
    const [originSegment, setOriginSegment] = useState<Models.Segment>(new Models.Segment({}))
    const [destinationSegment, setDestinationSegment] = useState<Models.Segment>(new Models.Segment({}))

    React.useEffect(() => {
        const allMilestones: Models.Milestone[] = []

        segments.forEach(segment => {
            if (segment.milestones) {
                const milestones: Models.Milestone[] = segment.milestones
                    .map(m => {
                        m.sequenceNo = segment.segmentSequenceNo
                        m.dateTime = Utils.getDateTimeForMilestone(m)
                        if (segment.status === 'Completed') {
                            m.status = 'Completed'
                        }
                        if (m.scheduledEpochTime) {
                            m.timeLabel = 'Planned'
                        }
                        if (m.estimatedEpochTime) {
                            m.timeLabel = 'Forecast'
                        }
                        if (m.actualEpochTime) {
                            m.timeLabel = ''
                        }

                        return m
                    })
                    // TODO comment for Kerry demo. Add condition by account settings after implement account support in Widget
                    .filter(m => config?.isShowEmptyMilestones || m.dateTime?.time !== '--:--')
                    .sort(Utils.sortMilestones)
                allMilestones.push(...milestones)
            }
        })

        let previousMilestone: Models.Milestone
        allMilestones.forEach(m => {
            if (m.dateTime?.date) {
                if (!previousMilestone) {
                    setStartShipmentDate(m.dateTime.date)
                }
                if (previousMilestone && previousMilestone.dateTime?.date) {
                    m.blueArrow = m.dateTime.date !== previousMilestone.dateTime.date
                }
                previousMilestone = m
            }
        })

        segments.forEach(segment => {
            segment.milestonesForRendering = allMilestones
                .filter(m => m.sequenceNo === segment.segmentSequenceNo)
                .sort(Utils.sortMilestones)
        })

        setOriginSegment(segments[0] || new Models.Segment({ segmentSequenceNo: 0 }))
        setDestinationSegment(segments[segments.length - 1] || new Models.Segment({ segmentSequenceNo: 1 }))

        if (
            // !!prevSegmentsCountRef.current &&
            prevSegmentsCountRef.current !== segments.length &&
            onChangeSegmentsCount &&
            segments[segments.length - 1]
        ) {
            onChangeSegmentsCount(segments[segments.length - 1].id)
        }

        prevSegmentsCountRef.current = segments.length
    }, [config?.isShowEmptyMilestones, segments, onChangeSegmentsCount])

    const segmentsForDraw = useMemo(() => {
        if (Services.DeviceConfigService.DeviceConfig.getIsTimeLineClear()) {
            return segments.slice(1, segments.length - 1).filter((segment, index, arr) => {
                if (arr[index + 1]) {
                    if (
                        arr[index + 2] &&
                        segment.routeMode === 'Road' &&
                        arr[index + 1].routeMode === 'Air' &&
                        arr[index + 2].routeMode === 'Road'
                    ) {
                        return false // ROAD,air,road
                    }
                    if (
                        arr[index - 1] &&
                        segment.routeMode === 'Air' &&
                        arr[index + 1].routeMode === 'Road' &&
                        arr[index - 1].routeMode === 'Road'
                    ) {
                        return true // road,AIR,road
                    }

                    return true
                }
                if (
                    arr[index - 2] &&
                    arr[index - 1] &&
                    segment.routeMode === 'Road' &&
                    arr[index - 1].routeMode === 'Air' &&
                    arr[index - 2].routeMode === 'Road'
                ) {
                    return false // road,air,ROAD
                }

                return true
            })
        }

        return segments.slice(1, segments.length - 1)
    }, [segments])

    if (segments.length === 0) {
        return (
            <div className={classes.timeLine__noData}>
                {noDataText ?? (
                    <>
                        No Data
                        <br />
                        for timeline
                    </>
                )}
            </div>
        )
    }

    return (
        <div className={classes.timeLine}>
            <div className={classes.timeLine__column}>
                {startShipmentDate && (
                    <div className={[classes.timeLine__timeArrow, classes.timeArrow].join(' ')}>
                        <div className={classes.timeArrow__row}>
                            <div className={classes.timeArrow__startDate}>{startShipmentDate}</div>
                        </div>
                    </div>
                )}

                {destinationSegment.segmentSequenceNo !== 1 && (
                    <div>
                        <ShipmentTimelineSegment
                            segment={originSegment}
                            type={'origin'}
                            name={config?.origin}
                            icons={config?.icons}
                        />
                    </div>
                )}

                {segmentsForDraw.map(segment => (
                    <div key={segment.id} data-key={segment.id}>
                        <ShipmentTimelineSegment segment={segment} icons={config?.icons} />
                    </div>
                ))}

                <div data-key={destinationSegment.id}>
                    <ShipmentTimelineSegment
                        segment={destinationSegment}
                        type={'destination'}
                        name={config?.destination}
                        icons={config?.icons}
                    />
                </div>
            </div>
        </div>
    )
}
